import React, { useState, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { useDebouncedSearch } from '../hooks/useDebouncedSearch';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { payeeAPI } from '../services/api';
import PageHeader from './PageHeader';
import DeleteIconButton from './DeleteIconButton';
import PayeeForm from './PayeeForm';
import { hasPermission } from '../utils/permissions';
import './ListWithDetail.css';
import './PayeeList.css';

const PAGE_SIZE = 25;

function PayeeList() {
  const navigate = useNavigate();
  const { id: detailId } = useParams();
  const { tenantPath } = useTenantFromUrl();
  const [payees, setPayees] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nameSearch, setNameSearch] = useState('');
  const [emailSearch, setEmailSearch] = useState('');
  const sentinelRef = useRef(null);
  const filtersRef = useRef({ nameSearch, emailSearch });
  filtersRef.current = { nameSearch, emailSearch };
  const page1RequestIdRef = useRef(0);

  const canCreatePayee = hasPermission('payees.create');
  const canDeletePayee = hasPermission('payees.delete');

  const loadPayees = useCallback(async (pageNum = 1, append = false) => {
    const snap = { ...filtersRef.current };
    let page1Id;
    if (pageNum === 1 && !append) {
      page1Id = ++page1RequestIdRef.current;
    }
    try {
      if (pageNum === 1 && !append) setLoading(true);
      else setLoadingMore(true);
      const params = { page: pageNum, pageSize };
      if (snap.nameSearch.trim()) params.q = snap.nameSearch.trim();
      if (snap.emailSearch.trim()) params.email_q = snap.emailSearch.trim();
      const response = await payeeAPI.getAll(params);
      if (pageNum === 1 && !append) {
        if (page1Id !== page1RequestIdRef.current) return;
      } else if (append) {
        const cur = filtersRef.current;
        if (snap.nameSearch !== cur.nameSearch || snap.emailSearch !== cur.emailSearch) return;
      }
      const data = response.data.data ?? [];
      const newTotal = response.data.total ?? 0;
      if (append) setPayees((prev) => [...prev, ...data]);
      else setPayees(data);
      setTotal(newTotal);
      setPage(pageNum);
    } catch (error) {
      if (pageNum === 1 && !append && page1Id !== page1RequestIdRef.current) return;
      if (append) {
        const cur = filtersRef.current;
        if (snap.nameSearch !== cur.nameSearch || snap.emailSearch !== cur.emailSearch) return;
      }
      console.error('Error loading payees:', error);
      const status = error.response?.status;
      const apiErr = error.response?.data?.error;
      if (status === 403) {
        alert(
          'You do not have permission to view payees (payees.read). Ask an admin to run the payee-permissions migration on this database, or add payees.read to your role.'
        );
      } else if (status === 401) {
        alert('Not signed in or session expired. Sign in again and retry.');
      } else {
        alert(apiErr || error.message || 'Failed to load payees');
      }
    } finally {
      if (pageNum === 1 && !append) {
        if (page1Id === page1RequestIdRef.current) setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  }, [pageSize]);

  useDebouncedSearch(`${nameSearch}|${emailSearch}`, () => loadPayees(1));

  const hasMore = payees.length < total && total > 0;
  const loadMore = () => { if (!loading && !loadingMore && hasMore) loadPayees(page + 1, true); };
  useInfiniteScroll(sentinelRef, loadMore, hasMore, loading || loadingMore);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this payee? Providers linked to it may need to be updated.')) {
      return;
    }
    try {
      await payeeAPI.delete(id);
      if (String(id) === String(detailId)) navigate(tenantPath('/lists/payees'));
      setPayees((prev) => prev.filter((p) => p.id !== id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (error) {
      console.error('Error deleting payee:', error);
      alert(error.response?.data?.error || 'Failed to delete payee');
    }
  };

  const colCount = 3 + (canDeletePayee ? 1 : 0);
  const showPanel = detailId != null;

  return (
    <div className={`list-with-detail ${showPanel ? 'list-with-detail--panel-open' : ''}`}>
    <div className="payee-list list-with-detail__list">
      <div className="header">
        <PageHeader title="Payees">
          {canCreatePayee && (
            <button onClick={() => navigate(tenantPath('/lists/payees/new'))} className="btn-new">+ New Payee</button>
          )}
        </PageHeader>
      </div>

      <div className="payee-list-info">
        {loading && payees.length === 0 ? 'Loading...' : total === 0 ? 'No payees found' : total === 1 ? '1 payee found' : `${total} payees found`}
      </div>

      <div className="payee-list-table-wrap list-table-wrap">
        <table className="payee-list-table list-table">
          <thead>
            <tr className="list-header-labels">
              <th>Name</th>
              <th>Email</th>
              <th>Tax ID</th>
              {canDeletePayee && <th>Delete</th>}
            </tr>
            <tr className="list-filter-row payee-list-filter-row">
              <th>
                <input
                  type="text"
                  className="payee-list-filter-input"
                  placeholder="Search name or tax ID..."
                  value={nameSearch}
                  onChange={(e) => setNameSearch(e.target.value)}
                  aria-label="Filter by name or tax ID"
                />
              </th>
              <th>
                <input
                  type="text"
                  className="payee-list-filter-input"
                  placeholder="Search email..."
                  value={emailSearch}
                  onChange={(e) => setEmailSearch(e.target.value)}
                  aria-label="Filter by email"
                />
              </th>
              <th aria-hidden="true" />
              {canDeletePayee && <th />}
            </tr>
          </thead>
          <tbody>
            {loading && payees.length === 0 ? (
              <tr><td colSpan={colCount} className="no-data loading-cell">Loading payees...</td></tr>
            ) : payees.length === 0 ? (
              <tr><td colSpan={colCount} className="no-data">No payees found</td></tr>
            ) : (
              payees.map((payee) => (
                <tr
                  key={payee.id}
                  onClick={() => navigate(tenantPath(`/lists/payees/${payee.id}`))}
                  className="clickable-row"
                >
                  <td>{payee.name || '—'}</td>
                  <td>{payee.email || '—'}</td>
                  <td>{payee.tax_id || '—'}</td>
                  {canDeletePayee && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <DeleteIconButton
                        className="list-btn-delete"
                        onClick={() => handleDelete(payee.id)}
                      />
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {hasMore && <div ref={sentinelRef} className="infinite-scroll-sentinel" aria-hidden="true" />}
      {loadingMore && <div className="infinite-scroll-loading">Loading more...</div>}
    </div>

    {showPanel && (
      <div className="list-with-detail__panel">
        {/* key: reset form + auto-save state when switching payees */}
        <PayeeForm key={detailId} />
      </div>
    )}
    </div>
  );
}

export default PayeeList;
